import {
  FLIP_TRICKS,
  GRAB_TRICKS,
  GRIND_TRICKS,
  LIP_TRICKS,
  MANUAL_HOLD_TRICKS,
  MANUAL_POP_TRICKS,
  MANUAL_TRICKS,
  type Dir,
  type Dir8,
  type FlipTrick,
  type GrabTrick,
  type GrindTrick,
  type LipTrick,
  type ManualHoldTrick,
  type ManualPopTrick,
  type ManualTrick,
  SPECIAL_TRICKS,
  type SpecialTrick,
  stickDir,
  stickDir8,
} from "../tricks";
import type { StatId } from "../stats";
import {
  BMX_AIR,
  BMX_MANUAL,
  BMX_POP,
  BMX_RAIL,
  BMX_SPEED,
  BMX_SPIN,
  SCOOTER_AIR,
  SCOOTER_MANUAL,
  SCOOTER_POP,
  SCOOTER_RAIL,
  SCOOTER_SPEED,
  SCOOTER_SPIN,
  SKATES_MANUAL,
  SKATES_RAIL,
  SKATES_SPIN,
} from "./tuning";

// Rides (#141): the board and the BMX are rides in the backpack. Step on
// (L2 + R2) with one out and that's what you ride. The sim is one physics
// core for all of them; a ride is a table of differences: multipliers on
// what the stats scale (`Skater.scale`) and its own trick names. The
// board's multipliers are all missing (exactly today's tuning) and its
// tricks are the THPS tables, so riding the board is unchanged.
//
// The BMX (Dave Mirra Freestyle BMX on our buttons): □ (flip) whips and
// spins the bike, ○ (grab) takes a hand or foot off, △ grinds on the pegs (by direction, #138),
// up,down / down,up manual on the back or front wheel (grab holds a trick
// on either: X-Up Manual, X-Up Endo). Two stick presses
// and □ flip the bike head over heels without the meter (Backflip,
// Frontflip), and □ mid-flip whips it too (a Backflip Tailwhip); its
// specials are bigger body flips (#138); it has no nollie; grab + direction in a manual holds a hand or
// foot off, and grab reads the diagonals too (#138). The rest of its trick list is #138.
//
// The scooter (#139): kick to push, a low pop and quick spins. □ whips the
// deck round the bars (Tailwhip, Heel Whip), spins the bars (Barspin) or
// flips the deck under you (Bri Flip); ○ takes a hand or foot off; △ grinds
// on the deck; up,down manuals on the back wheel. No nollie, no specials yet.
//
// Roller skates (#140, aggressive inlines): stride to push, and with no board
// under you the stance is free: rolling backwards is no harder than forwards
// (`stanceFree`: no switch penalty). □ spins and flips the body (Flatspin,
// Misty Flip, Rodeo Flip...), ○ grabs a skate (Mute, Japan, Stale, Rocket), △
// is a soul plate trick, up,down manuals on the heel wheels.

export type RideId = "board" | "bmx" | "scooter" | "skates";

export interface RideTricks {
  flip: Readonly<Record<Dir8, FlipTrick>>;
  grab: Readonly<Record<Dir, GrabTrick>>;
  /** Grabs on the diagonals; null when the ride has none (a diagonal reads as its nearest straight direction). */
  grabDiag: Readonly<Record<Diagonal, GrabTrick>> | null;
  /** Grabs pulled with L1 / R1 held (DMFB's modifier, #138); null when the ride has no modifier (the shoulders only spin). */
  modGrab: Readonly<Record<Dir, GrabTrick>> | null;
  grind: Readonly<Record<Dir8, GrindTrick>>;
  lip: Readonly<Record<Dir, LipTrick>>;
  manual: Readonly<{ manual: ManualTrick; nose: ManualTrick }>;
  manualPop: Readonly<Record<Dir8, ManualPopTrick>>;
  /** Held ground tricks from a manual; null when the ride has none (grab does nothing in a manual). */
  manualHold: Readonly<Record<Dir, ManualHoldTrick>> | null;
  /** Held tricks from a nose manual (the bike's endo, #138); null: the same as `manualHold`. */
  noseHold: Readonly<Record<Dir, ManualHoldTrick>> | null;
  /** Flips head over heels on two stick presses and flip, meter or not (empty: none). */
  bodyFlips: readonly BodyFlip[];
  /** The special meter's tricks on this ride (empty: none). */
  specials: readonly SpecialTrick[];
  /** Whether L2 toggles nollie stance on this ride. */
  nollie: boolean;
  /**
   * No stance to swap (skates #140, the BMX and scooter #171): riding switch
   * carries no penalty or bonus (`Skater.switchPenalty`, `ridingSwitch`), no
   * "Switch" in trick names and no switch read-out (`stanceReadout`).
   */
  stanceFree: boolean;
}

export type Diagonal = Exclude<Dir8, Dir>;

/**
 * An everyday body flip (#138): pulled like a special (two stick presses, then
 * flip) but with no meter, and it runs by itself the same way: land before it
 * comes round and you bail. Flip again during it to whip the bike too.
 */
export interface BodyFlip {
  name: string;
  points: number;
  seq: readonly [Dir, Dir];
  /** Seconds it takes. */
  duration: number;
  /** Whole flips: > 0 backward, < 0 forward. */
  flips: number;
}

export interface Ride {
  id: RideId;
  name: string;
  /** Multipliers on what a stat scales; a stat not listed is untouched. */
  scales: Readonly<Partial<Record<StatId, number>>>;
  tricks: RideTricks;
}

const BOARD_TRICKS: RideTricks = {
  flip: FLIP_TRICKS,
  grab: GRAB_TRICKS,
  grabDiag: null,
  modGrab: null,
  grind: GRIND_TRICKS,
  lip: LIP_TRICKS,
  manual: MANUAL_TRICKS,
  manualPop: MANUAL_POP_TRICKS,
  manualHold: MANUAL_HOLD_TRICKS,
  noseHold: null,
  bodyFlips: [],
  specials: SPECIAL_TRICKS,
  nollie: true,
  stanceFree: false,
};

// The bike's flips reuse the board's animation ids; the bike model reads them
// as its own moves (`render/bike.ts`): kickflip a tailwhip, heelflip a
// barspin, impossible a turndown, varial a reverse tailwhip, treflip a
// tailwhip and barspin, hardflip a turndown and barspin. A double tap makes
// it a Double, as on the board.
const TAILWHIP: FlipTrick = { name: "Tailwhip", points: 150, anim: "kickflip", duration: 0.5 };
const REVERSE_WHIP: FlipTrick = { name: "Reverse Tailwhip", points: 175, anim: "varial", duration: 0.52 };
const BMX_FLIPS: Record<Dir8, FlipTrick> = {
  n: TAILWHIP,
  l: TAILWHIP,
  r: { name: "Barspin", points: 125, anim: "heelflip", duration: 0.42 },
  u: { name: "Turndown", points: 125, anim: "impossible", duration: 0.45 },
  d: REVERSE_WHIP,
  ul: { name: "Tailwhip Barspin", points: 250, anim: "treflip", duration: 0.56 },
  ur: { name: "Turndown Barspin", points: 200, anim: "hardflip", duration: 0.5 },
  dl: REVERSE_WHIP,
  dr: { name: "Reverse Whip Barspin", points: 275, anim: "varialheel", duration: 0.58 },
};

const BMX_GRABS: Record<Dir, GrabTrick> = {
  n: { name: "No-Hander", points: 250, perSecond: 300 },
  u: { name: "X-Up", points: 200, perSecond: 250 },
  d: { name: "Superman", points: 250, perSecond: 300 },
  l: { name: "One-Footer", points: 200, perSecond: 250 },
  r: { name: "Can-Can", points: 200, perSecond: 250 },
};

/**
 * The bike's grabs on the diagonals (#138), from DMFB's list: both feet off
 * kicked out wide (No-Footer), the bike flattened sideways under you
 * (Tabletop), a Superman with one hand back on the seat (Superman Seat
 * Grab) and a leg swung over the back wheel to the other side (Nac-Nac).
 * The bike pose reads the names (`render/rider/bike-pose.ts`).
 */
const BMX_GRAB_DIAGONALS: Record<Diagonal, GrabTrick> = {
  ul: { name: "No-Footer", points: 300, perSecond: 350 },
  ur: { name: "Tabletop", points: 350, perSecond: 400 },
  dl: { name: "Superman Seat Grab", points: 350, perSecond: 400 },
  dr: { name: "Nac-Nac", points: 300, perSecond: 350 },
};

/**
 * DMFB's modifier (#138): L1 or R1 held as you grab pulls the bigger version
 * of the grab at that direction, two of the bike's hand and foot offs at
 * once, worth more than either. Both hands and feet off (Nothing), an X-Up
 * with a leg swung over the back (X-Up Nac-Nac), a Superman Seat Grab
 * stretched right out (Hart Attack), a foot off with the bars crossed
 * (One-Foot X-Up) and a Can-Can with the hands off (No-Hand Can-Can). The
 * shoulders still spin you while they're held, so a modified grab comes
 * with as much spin as you hold them for. The stick reads straight
 * directions only here. Posed as the parts they're made of
 * (`render/rider/bike-pose.ts`'s `BIKE_PARTS`).
 */
const BMX_MOD_GRABS: Record<Dir, GrabTrick> = {
  n: { name: "Nothing", points: 550, perSecond: 600 },
  u: { name: "X-Up Nac-Nac", points: 450, perSecond: 500 },
  d: { name: "Hart Attack", points: 550, perSecond: 600 },
  l: { name: "One-Foot X-Up", points: 400, perSecond: 450 },
  r: { name: "No-Hand Can-Can", points: 450, perSecond: 500 },
};

/**
 * Peg grinds by direction (#138), on the board's grind layout: each keeps the
 * board's grind style at its direction, which the bike pose reads
 * (`render/rider/bike-pose.ts`'s `PEG_POSES`). Both pegs on the rail
 * (Double Peg), the front peg only (Toothhang) or the back one (Icepick), the
 * crank on it (Crankarm), the back peg with the front wheel over the rail
 * (Feeble, Luc-E, Unlucky) or tucked in on your side (Toothpick), the front
 * peg with the back wheel dropped (Smith).
 */
const BMX_GRINDS: Record<Dir8, GrindTrick> = {
  n: { name: "Double Peg", points: 150, perSecond: 225, style: "fiftyfifty" },
  u: { name: "Toothhang", points: 200, perSecond: 275, style: "nosegrind" },
  d: { name: "Icepick", points: 200, perSecond: 275, style: "fiveo" },
  l: { name: "Crankarm", points: 200, perSecond: 275, style: "boardslide" },
  r: { name: "Luc-E", points: 250, perSecond: 300, style: "lipslide" },
  ul: { name: "Toothpick", points: 225, perSecond: 300, style: "crooked" },
  ur: { name: "Unlucky", points: 275, perSecond: 325, style: "overcrook" },
  dl: { name: "Smith", points: 225, perSecond: 300, style: "smith" },
  dr: { name: "Feeble", points: 225, perSecond: 300, style: "feeble" },
};

/** On the coping: a back peg (Tooth-Pick), the front one (Nose-Pick), both wheels' tyres (Pedal Stall)... */
const BMX_LIPS: Record<Dir, LipTrick> = {
  n: { name: "Tooth-Pick", points: 200, perSecond: 200, style: "axle", fakie: false },
  u: { name: "X-Up Stall", points: 300, perSecond: 225, style: "axle", fakie: false },
  d: { name: "Tooth-Pick to Fakie", points: 250, perSecond: 200, style: "rock", fakie: true },
  l: { name: "Pedal Stall", points: 250, perSecond: 200, style: "axle", fakie: false },
  r: { name: "Nose-Pick", points: 250, perSecond: 200, style: "nose", fakie: false },
};

/** Flatground from a manual: a quick whip or spin, landing back on the one wheel. */
const BMX_MANUAL_POPS: Record<Dir8, ManualPopTrick> = {
  n: { name: "Tailwhip", points: 150, anim: "kickflip", rotations: 1, duration: 0.4 },
  l: { name: "Tailwhip", points: 150, anim: "kickflip", rotations: 1, duration: 0.4 },
  r: { name: "Barspin", points: 100, anim: "heelflip", rotations: 1, duration: 0.34 },
  u: { name: "Turndown", points: 100, anim: "impossible", rotations: 1, duration: 0.36 },
  d: { name: "Reverse Tailwhip", points: 175, anim: "varial", rotations: 1, duration: 0.42 },
  ul: { name: "Tailwhip Barspin", points: 250, anim: "treflip", rotations: 1, duration: 0.46 },
  ur: { name: "Turndown Barspin", points: 200, anim: "hardflip", rotations: 1, duration: 0.42 },
  dl: { name: "Reverse Tailwhip", points: 175, anim: "varial", rotations: 1, duration: 0.42 },
  dr: { name: "Reverse Whip Barspin", points: 275, anim: "varialheel", rotations: 1, duration: 0.48 },
};

/**
 * Held from a manual on grab + direction (#138), as the board's Casper and
 * Pogo are: a hand or foot off while up on the one wheel. The bike pose
 * reads the names (`render/rider/bike-pose.ts`'s `BIKE_PARTS`), so `pose`
 * leaves the board alone.
 */
const STILL_BOARD = { pitch: 0, roll: 0, lift: 0, hand: null } as const;
const BMX_MANUAL_HOLDS: Record<Dir, ManualHoldTrick> = {
  n: { name: "No-Hand Manual", points: 200, perSecond: 250, pose: STILL_BOARD },
  u: { name: "X-Up Manual", points: 200, perSecond: 250, pose: STILL_BOARD },
  d: { name: "Can-Can Manual", points: 200, perSecond: 250, pose: STILL_BOARD },
  l: { name: "One-Foot Manual", points: 200, perSecond: 250, pose: STILL_BOARD },
  r: { name: "One-Foot No-Hander", points: 300, perSecond: 350, pose: STILL_BOARD },
};

/**
 * Held from an endo (a nose manual, up on the front wheel) on grab +
 * direction (#138): the manual's hand and foot offs, worth more because the
 * front wheel is the harder balance, and a Nac-Nac (a leg swung over the
 * back wheel) on the right. Posed by name like the manual's
 * (`render/rider/bike-pose.ts`'s `BIKE_PARTS`).
 */
const BMX_ENDO_HOLDS: Record<Dir, ManualHoldTrick> = {
  n: { name: "No-Hand Endo", points: 250, perSecond: 300, pose: STILL_BOARD },
  u: { name: "X-Up Endo", points: 250, perSecond: 300, pose: STILL_BOARD },
  d: { name: "Can-Can Endo", points: 250, perSecond: 300, pose: STILL_BOARD },
  l: { name: "One-Foot Endo", points: 250, perSecond: 300, pose: STILL_BOARD },
  r: { name: "Endo Nac-Nac", points: 350, perSecond: 400, pose: STILL_BOARD },
};

/**
 * The bike's everyday flips (#138): up, down and □ backflips, down, up and □
 * frontflips (the manual's sequences, so the stick does what it does on the
 * wheels: up pulls back). Each takes a hair less than the highest flat bunny
 * hop stays up without the meter (~1.03s), so off flat you'd have to pull it
 * the instant you leave the ground; they want a kicker or vert, where a
 * crouched air off the park's quarter (~1.2s) leaves a quarter second to
 * pull it. □
 * again mid-flip whips the bike too (`sim/air.ts`'s `stepFlip`): a Backflip
 * Tailwhip, a Frontflip Barspin.
 */
const BMX_BODY_FLIPS: readonly BodyFlip[] = [
  { name: "Backflip", points: 600, seq: ["u", "d"], duration: 0.95, flips: 1 },
  { name: "Frontflip", points: 700, seq: ["d", "u"], duration: 0.95, flips: -1 },
];

/**
 * The bike's specials (#138): bigger flips head over heels, pulled like the
 * board's spin specials (meter full, two stick presses, grab). Each runs by
 * itself, hands on the bars, and you bail if you're down before it comes
 * round: a Flair (a backflip with a 180, which brings you back down a
 * quarter) wants vert, where a cruise-speed air with the meter full stays up
 * ~1.2s, and the Doubles need that air crouched (~1.35s). The hands stay on
 * the grips (`hand: null`).
 */
const BMX_SPECIALS: readonly SpecialTrick[] = [
  { kind: "spin", name: "Flair", points: 2000, seq: ["r", "d"], button: "grab", duration: 1.15, spin: 180, invert: false, flips: 1, hand: null },
  { kind: "spin", name: "Double Backflip", points: 3500, seq: ["l", "d"], button: "grab", duration: 1.3, spin: 0, invert: false, flips: 2, hand: null },
  { kind: "spin", name: "Double Frontflip", points: 4000, seq: ["d", "u"], button: "grab", duration: 1.3, spin: 0, invert: false, flips: -2, hand: null },
];

const BMX_TRICKS: RideTricks = {
  flip: BMX_FLIPS,
  grab: BMX_GRABS,
  grabDiag: BMX_GRAB_DIAGONALS,
  modGrab: BMX_MOD_GRABS,
  grind: BMX_GRINDS,
  lip: BMX_LIPS,
  manual: MANUAL_TRICKS,
  manualPop: BMX_MANUAL_POPS,
  manualHold: BMX_MANUAL_HOLDS,
  noseHold: BMX_ENDO_HOLDS,
  bodyFlips: BMX_BODY_FLIPS,
  specials: BMX_SPECIALS,
  nollie: false,
  stanceFree: true,
};

// The scooter's whips reuse the board's animation ids too, read as its own
// moves by the scooter model (`render/rider/scooter-pose.ts`'s `whipScooter`):
// kickflip a tailwhip (the deck round the stem), varial a heel whip (the
// other way, kicked round by the heel), heelflip a barspin, impossible a bri
// flip (the deck flipped round under the bars), treflip a finger whip (the
// whole scooter spun round the stem, hands off), hardflip and varialheel a
// whip with a barspin. Quicker than the bike's: it's a lot lighter.
const SCOOTER_TAILWHIP: FlipTrick = { name: "Tailwhip", points: 150, anim: "kickflip", duration: 0.42 };
const HEEL_WHIP: FlipTrick = { name: "Heel Whip", points: 175, anim: "varial", duration: 0.45 };
const SCOOTER_FLIPS: Record<Dir8, FlipTrick> = {
  n: SCOOTER_TAILWHIP,
  l: SCOOTER_TAILWHIP,
  r: { name: "Barspin", points: 125, anim: "heelflip", duration: 0.36 },
  u: { name: "Bri Flip", points: 250, anim: "impossible", duration: 0.5 },
  d: HEEL_WHIP,
  ul: { name: "Finger Whip", points: 250, anim: "treflip", duration: 0.5 },
  ur: { name: "Tailwhip Barspin", points: 250, anim: "hardflip", duration: 0.5 },
  dl: HEEL_WHIP,
  dr: { name: "Heel Whip Barspin", points: 275, anim: "varialheel", duration: 0.52 },
};

/** Hands and feet off (#139), posed by name (`render/rider/scooter-pose.ts`'s `SCOOTER_POSE_PARTS`). */
const SCOOTER_GRABS: Record<Dir, GrabTrick> = {
  n: { name: "No-Hander", points: 250, perSecond: 300 },
  u: { name: "Superman", points: 300, perSecond: 350 },
  d: { name: "No-Footer", points: 250, perSecond: 300 },
  l: { name: "One-Footer", points: 200, perSecond: 250 },
  r: { name: "Can-Can", points: 200, perSecond: 250 },
};

/**
 * Deck grinds by direction (#139), on the board's grind layout: the deck on
 * the rail along its length (50-50), on its front or back end (Nosegrind,
 * 5-0), across it (Boardslide, Lipslide), on the back end with the front
 * dropped to your side (Smith) or over the far side (Feeble), or on the
 * front end angled in or out (Crooked, Overcrook). The scooter pose reads
 * the style (`render/rider/scooter-pose.ts`'s `DECK_GRINDS`).
 */
const SCOOTER_GRINDS: Record<Dir8, GrindTrick> = {
  n: { name: "50-50", points: 100, perSecond: 200, style: "fiftyfifty" },
  u: { name: "Nosegrind", points: 175, perSecond: 250, style: "nosegrind" },
  d: { name: "5-0", points: 150, perSecond: 225, style: "fiveo" },
  l: { name: "Boardslide", points: 150, perSecond: 225, style: "boardslide" },
  r: { name: "Lipslide", points: 200, perSecond: 250, style: "lipslide" },
  ul: { name: "Crooked", points: 200, perSecond: 275, style: "crooked" },
  ur: { name: "Overcrook", points: 250, perSecond: 300, style: "overcrook" },
  dl: { name: "Smith", points: 225, perSecond: 300, style: "smith" },
  dr: { name: "Feeble", points: 225, perSecond: 300, style: "feeble" },
};

/** On the coping: the back wheel (Tail Stall), the front (Nose Stall), the deck across it (Disaster)... */
const SCOOTER_LIPS: Record<Dir, LipTrick> = {
  n: { name: "Tail Stall", points: 200, perSecond: 200, style: "axle", fakie: false },
  u: { name: "Disaster", points: 300, perSecond: 225, style: "disaster", fakie: false },
  d: { name: "Fakie Tail Stall", points: 250, perSecond: 200, style: "rock", fakie: true },
  l: { name: "Deck Stall", points: 250, perSecond: 200, style: "axle", fakie: false },
  r: { name: "Nose Stall", points: 250, perSecond: 200, style: "nose", fakie: false },
};

/** Flatground from a manual: a quick whip or spin, landing back on the back wheel. */
const SCOOTER_MANUAL_POPS: Record<Dir8, ManualPopTrick> = {
  n: { name: "Tailwhip", points: 150, anim: "kickflip", rotations: 1, duration: 0.36 },
  l: { name: "Tailwhip", points: 150, anim: "kickflip", rotations: 1, duration: 0.36 },
  r: { name: "Barspin", points: 100, anim: "heelflip", rotations: 1, duration: 0.3 },
  u: { name: "Bri Flip", points: 250, anim: "impossible", rotations: 1, duration: 0.42 },
  d: { name: "Heel Whip", points: 175, anim: "varial", rotations: 1, duration: 0.38 },
  ul: { name: "Finger Whip", points: 250, anim: "treflip", rotations: 1, duration: 0.42 },
  ur: { name: "Tailwhip Barspin", points: 250, anim: "hardflip", rotations: 1, duration: 0.42 },
  dl: { name: "Heel Whip", points: 175, anim: "varial", rotations: 1, duration: 0.38 },
  dr: { name: "Heel Whip Barspin", points: 275, anim: "varialheel", rotations: 1, duration: 0.44 },
};

/**
 * The scooter's everyday flips (#139), on the bike's sequences: up, down and
 * □ backflips, down, up and □ frontflips. A touch quicker than the bike's,
 * but the low pop means they want a quarter's air all the same.
 */
const SCOOTER_BODY_FLIPS: readonly BodyFlip[] = [
  { name: "Backflip", points: 600, seq: ["u", "d"], duration: 0.9, flips: 1 },
  { name: "Frontflip", points: 700, seq: ["d", "u"], duration: 0.9, flips: -1 },
];

const SCOOTER_TRICKS: RideTricks = {
  flip: SCOOTER_FLIPS,
  grab: SCOOTER_GRABS,
  grabDiag: null,
  modGrab: null,
  grind: SCOOTER_GRINDS,
  lip: SCOOTER_LIPS,
  manual: MANUAL_TRICKS,
  manualPop: SCOOTER_MANUAL_POPS,
  manualHold: null,
  noseHold: null,
  bodyFlips: SCOOTER_BODY_FLIPS,
  specials: [],
  nollie: false,
  stanceFree: true,
};

// The skates' □ tricks (#140) are the body thrown round, read off the board's
// animation ids by the skates' pose (`render/rider/skates-pose.ts`'s
// `SKATE_SPINS`): kickflip a flatspin (an off-axis backflip laid flat),
// varial a misty flip (a forward flip thrown sideways), heelflip a rodeo
// flip (a backflip with a 180), impossible a corkscrew, varialheel a bio
// flip. They take a while: off flat you'd pull them the moment you leave
// the ground, so they want a kicker or vert.
const FLATSPIN: FlipTrick = { name: "Flatspin", points: 400, anim: "kickflip", duration: 0.62 };
const MISTY: FlipTrick = { name: "Misty Flip", points: 450, anim: "varial", duration: 0.64 };
const RODEO: FlipTrick = { name: "Rodeo Flip", points: 450, anim: "heelflip", duration: 0.64 };
const BIO: FlipTrick = { name: "Bio Flip", points: 500, anim: "varialheel", duration: 0.66 };
const SKATE_FLIPS: Record<Dir8, FlipTrick> = {
  n: FLATSPIN,
  l: MISTY,
  r: RODEO,
  u: { name: "Corkscrew", points: 400, anim: "impossible", duration: 0.62 },
  d: BIO,
  ul: MISTY,
  ur: RODEO,
  dl: FLATSPIN,
  dr: BIO,
};

/** A skate grabbed (#140), posed by name (`render/rider/skates-pose.ts`'s `SKATE_GRABS`). */
const SKATE_GRABS: Record<Dir, GrabTrick> = {
  n: { name: "Mute", points: 250, perSecond: 300 },
  u: { name: "Japan", points: 300, perSecond: 350 },
  d: { name: "Rocket", points: 300, perSecond: 350 },
  l: { name: "Stale", points: 250, perSecond: 300 },
  r: { name: "Liu Kang", points: 250, perSecond: 300 },
};

/**
 * Soul plate tricks by direction (#140), each keeping the board's grind style
 * at its direction, which the skates' pose reads (`SOUL_POSES`): Soul (the
 * front skate's plate along the rail, the back skate's frame across it),
 * Topsoul (the front skate rolled over on its top side), Makio (one skate's
 * plate, the other foot held up), Frontside and Backside (both frames across
 * the rail, facing or backing it), Royale, Unity (the feet crossed), Torque
 * Soul and Sweatstance (the plate on the back foot, turned out).
 */
const SKATE_GRINDS: Record<Dir8, GrindTrick> = {
  n: { name: "Soul", points: 150, perSecond: 225, style: "fiftyfifty" },
  u: { name: "Topsoul", points: 200, perSecond: 275, style: "nosegrind" },
  d: { name: "Makio", points: 200, perSecond: 275, style: "fiveo" },
  l: { name: "Frontside", points: 150, perSecond: 225, style: "boardslide" },
  r: { name: "Backside", points: 175, perSecond: 250, style: "lipslide" },
  ul: { name: "Royale", points: 225, perSecond: 300, style: "crooked" },
  ur: { name: "Unity", points: 275, perSecond: 325, style: "overcrook" },
  dl: { name: "Torque Soul", points: 225, perSecond: 300, style: "smith" },
  dr: { name: "Sweatstance", points: 250, perSecond: 300, style: "feeble" },
};

/** On the coping: the soul plate (Soul Stall), the toes (Toe Stall), the frames across it... */
const SKATE_LIPS: Record<Dir, LipTrick> = {
  n: { name: "Soul Stall", points: 200, perSecond: 200, style: "axle", fakie: false },
  u: { name: "Backside Stall", points: 300, perSecond: 225, style: "disaster", fakie: false },
  d: { name: "Fakie Soul Stall", points: 250, perSecond: 200, style: "rock", fakie: true },
  l: { name: "Frontside Stall", points: 250, perSecond: 200, style: "axle", fakie: false },
  r: { name: "Toe Stall", points: 250, perSecond: 200, style: "nose", fakie: false },
};

/** From a manual (#140): a hop spun round, landing back on the heels. */
const HOP_360: ManualPopTrick = { name: "Hop 360", points: 150, anim: "kickflip", rotations: 1, duration: 0.36 };
const SKATE_MANUAL_POPS: Record<Dir8, ManualPopTrick> = {
  n: HOP_360,
  l: HOP_360,
  r: HOP_360,
  u: HOP_360,
  d: { name: "Hop 540", points: 250, anim: "varial", rotations: 1, duration: 0.44 },
  ul: HOP_360,
  ur: HOP_360,
  dl: HOP_360,
  dr: HOP_360,
};

/** The skates' everyday flips (#140), on the bike's sequences. */
const SKATE_BODY_FLIPS: readonly BodyFlip[] = [
  { name: "Backflip", points: 600, seq: ["u", "d"], duration: 0.85, flips: 1 },
  { name: "Frontflip", points: 700, seq: ["d", "u"], duration: 0.85, flips: -1 },
];

const SKATE_TRICKS: RideTricks = {
  flip: SKATE_FLIPS,
  grab: SKATE_GRABS,
  grabDiag: null,
  modGrab: null,
  grind: SKATE_GRINDS,
  lip: SKATE_LIPS,
  manual: MANUAL_TRICKS,
  manualPop: SKATE_MANUAL_POPS,
  manualHold: null,
  noseHold: null,
  bodyFlips: SKATE_BODY_FLIPS,
  specials: [],
  nollie: false,
  stanceFree: true,
};

export const RIDES: Readonly<Record<RideId, Ride>> = {
  board: { id: "board", name: "Board", scales: {}, tricks: BOARD_TRICKS },
  bmx: {
    id: "bmx",
    name: "BMX bike",
    scales: { ollie: BMX_POP, air: BMX_AIR, spin: BMX_SPIN, speed: BMX_SPEED, rail: BMX_RAIL, manual: BMX_MANUAL },
    tricks: BMX_TRICKS,
  },
  scooter: {
    id: "scooter",
    name: "Scooter",
    scales: { ollie: SCOOTER_POP, air: SCOOTER_AIR, spin: SCOOTER_SPIN, speed: SCOOTER_SPEED, rail: SCOOTER_RAIL, manual: SCOOTER_MANUAL },
    tricks: SCOOTER_TRICKS,
  },
  skates: {
    id: "skates",
    name: "Roller skates",
    scales: { spin: SKATES_SPIN, rail: SKATES_RAIL, manual: SKATES_MANUAL },
    tricks: SKATE_TRICKS,
  },
};

/**
 * The grab for the stick at the press: with the ride's modifier held (`mod`,
 * L1 / R1 on the bike) its modified grab, else a diagonal if the ride has
 * them and the stick is clearly on one.
 */
export function grabFor(tricks: RideTricks, x: number, y: number, mod = false): GrabTrick {
  if (mod && tricks.modGrab) return tricks.modGrab[stickDir(x, y)];
  if (tricks.grabDiag) {
    const d = stickDir8(x, y);
    if (d.length === 2) return tricks.grabDiag[d as Diagonal];
  }
  return tricks.grab[stickDir(x, y)];
}

/** The ride's body flip a two-press stick sequence pulls, if any. */
export function bodyFlipFor(tricks: RideTricks, seq: readonly [Dir, Dir] | null): BodyFlip | null {
  if (!seq) return null;
  return tricks.bodyFlips.find((f) => f.seq[0] === seq[0] && f.seq[1] === seq[1]) ?? null;
}

export function isRide(id: string): id is RideId {
  return Object.hasOwn(RIDES, id);
}

/**
 * What a ride's trick tables hold, for anything that lists them (#139): `air`
 * starts an air (a spin can be credited to it), `ground` starts on a rail, a
 * lip or a manual, `pop` hops out of a manual, and `held` is held hands and
 * feet (a grab, a manual hold), which the rider poses part by part.
 */
export type RideTrickTag = "air" | "ground" | "pop" | "held";

/**
 * Each `RideTricks` field's tags. Typed against every key, so a new table
 * doesn't compile until it's said what it holds, and the tasks, the special
 * task and the pose coverage pick it up through `rideTricks`. The specials
 * go by their kind (a grind special is ground, the rest air).
 */
const TABLE_TAGS: Readonly<Record<keyof RideTricks, readonly RideTrickTag[] | "special">> = {
  flip: ["air"],
  grab: ["air", "held"],
  grabDiag: ["air", "held"],
  modGrab: ["air", "held"],
  grind: ["ground"],
  lip: ["ground"],
  manual: ["ground"],
  manualPop: ["pop"],
  manualHold: ["ground", "held"],
  noseHold: ["ground", "held"],
  bodyFlips: ["air"],
  specials: "special",
  nollie: [],
  stanceFree: [],
};

/** A ride's tricks with a tag, table by table in `RideTricks`' order. */
export function rideTricks(tricks: RideTricks, tag: RideTrickTag): { name: string }[] {
  return (Object.keys(TABLE_TAGS) as (keyof RideTricks)[]).flatMap((key) => {
    const tags = TABLE_TAGS[key];
    if (tags === "special") return tricks.specials.filter((x) => (x.kind === "grind" ? "ground" : "air") === tag);
    if (!tags.includes(tag)) return [];
    const table = tricks[key] as Readonly<Record<string, { name: string }>> | readonly { name: string }[] | null;
    return Object.values(table ?? {});
  });
}
